import React, { useEffect, useMemo, useRef } from 'react'
import { useDrop } from 'react-dnd'
import {
  changeNewTemplateFilesValueRoutine,
  removeNewTemplateFilesValueRoutine
} from 'features/analytics/ducks/actions'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { selectAddTemplateFiles } from 'features/analytics/ducks/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import ClearIcon from '@mui/icons-material/Clear'
import { toast } from 'react-toastify'
import { groupFileTypesForTemplateDisplay } from 'features/analytics/utils/templateFiles'

const FilesDropContainer = ({
  isEdit,
  fileTypesValues,
  setFileTypesValues,
  isDefaultTemplate,
  isEditable = true,
  isLocked = false,
  readOnlyReason,
  onFileItemDropped,
  onFileItemRemoved
}) => {
  const dispatch = useDispatch()
  const selectedValues = useSelector(selectAddTemplateFiles)
  const fileCategories = useSelector(selectFileCategories)
  const toastCooldownRef = useRef(0)

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: 'template-file',
    drop: (item, monitor) => {
      const isReadOnly = !isEditable
      if (isReadOnly) {
        const now = Date.now()
        if (now - toastCooldownRef.current > 1500) {
          toast.info(readOnlyReason || (isLocked ? 'This template is set as non-editable by the creator.' : 'Default checklist is read-only.'))
          toastCooldownRef.current = now
        }
        return
      }
      if (!monitor.didDrop()) {
        if (isEdit) {
          const exists = fileTypesValues.some((v) => v.id === item.id)
          if (!exists) addValue(item)
        } else {
          const exists = selectedValues.some((v) => String(v.id) === String(item.id))
          if (!exists) addValue(item)
        }
        if (typeof onFileItemDropped === 'function') {
          onFileItemDropped(item)
        }
      }
    },
    collect: (monitor) => ({
      canDrop: isEditable && monitor.canDrop(),
      isOver: monitor.isOver()
    }),
    canDrop: () => isEditable
  })

  const addValue = value => {
    if (isEdit) {
      setFileTypesValues(prev => [ ...prev, value ])
    } else {
      dispatch(changeNewTemplateFilesValueRoutine(value))
    }
  }

  const removeValue = value => {
    if (isEdit) {
      setFileTypesValues(fileTypesValues.filter(type => type.id !== value))
    } else {
      dispatch(removeNewTemplateFilesValueRoutine(value))
    }

    if (typeof onFileItemRemoved === 'function') {
      onFileItemRemoved(value)
    }
  }

  const valuesToDisplay = useMemo(() => {
    if (selectedValues || fileTypesValues) {
      const values = isEdit ? fileTypesValues : selectedValues
      const categories = groupFileTypesForTemplateDisplay(values, fileCategories)

      return categories.map(category => {
        if (isNotNilOrEmpty(category.values)) {
          return (
            <React.Fragment key={category.id}>
              <CategoryName>{category.label}</CategoryName>
              {
                category.values.map(value => (
                  <SingleItem key={`${category.label}-${value.id}`}>
                    <div>
                      <span>•</span>{value.name}
                    </div>
                    <ItemActions>
                      <CrossIcon
                        disabled={isDefaultTemplate || !isEditable}
                        onClick={
                          isDefaultTemplate || !isEditable
                            ? () => {}
                            : () => removeValue(value.id)
                        }
                      />
                    </ItemActions>
                  </SingleItem>
                ))
              }
            </React.Fragment>
          )
        } else {
          return null
        }
      })
    }
  }, [selectedValues, fileTypesValues, fileCategories])

  const handleDropAreaAttempt = () => {
    if (!isEditable) {
      const now = Date.now()
      if (now - toastCooldownRef.current > 1500) {
        toast.info(readOnlyReason || (isLocked ? 'This template is set as non-editable by the creator.' : 'Default checklist is read-only.'))
        toastCooldownRef.current = now
      }
    }
  }

  return (
    <Content
      ref={drop}
      $isHighlighted={canDrop && isOver}
      $isDisabled={!isEditable}
      onMouseDownCapture={handleDropAreaAttempt}
      onTouchStartCapture={handleDropAreaAttempt}
      onDragEnterCapture={handleDropAreaAttempt}
      onDragOverCapture={(e) => {
        handleDropAreaAttempt()
        e.preventDefault()
      }}
    >
      <SectionTitle>files</SectionTitle>
      {valuesToDisplay}
      <>
        <DropHint>
          {isEditable ? 'Drag files here' : (isLocked ? 'Template is non-editable' : 'Template is read-only')}
        </DropHint>
        <DropContainer
          isHighlighted={canDrop && isOver}
          onMouseDown={handleDropAreaAttempt}
          onTouchStart={handleDropAreaAttempt}
        />
      </>
    </Content>
  )
}

export default FilesDropContainer

const Content = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  padding: 20px;
  border-radius: 6px;
  transition: 0.2s all;
  min-height: 120px;
  position: relative;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};

  @media (min-width: 769px) {
    border: 2px dashed
      ${({ theme, $isHighlighted }) =>
        $isHighlighted ? theme.colors.primary : 'rgba(0,0,0,0.08)'};
    background: ${({ $isHighlighted }) =>
      $isHighlighted ? '#eaf9fa' : 'transparent'};
  }
`

const SingleItem = styled.div`
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;

  span {
    display: inline-block;
    color: ${({ theme }) => theme.colors.secondary};
    margin-right: 10px;
  }
`

const SectionTitle = styled.div`
  text-transform: uppercase;
  margin-bottom: 10px;
`

const CategoryName = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  margin: 15px 0 10px;
`

const CrossIcon = styled(ClearIcon)`
  font-size: 14px !important;
  cursor: pointer;
  color: ${({ disabled, theme }) =>  disabled ? theme.colors.grey[600] : theme.colors.primary};
`

const ItemActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const DropHint = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;

  @media (max-width: 768px) {
    display: none;
  }
`

const DropContainer = styled.div`
  display: block;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.grey[600]};
  margin-top: 20px;
  transition: .3s all;
  background-color: ${({ isHighlighted }) => isHighlighted ? '#eaf9fa' : 'transparent'};

  @media (min-width: 769px) {
    display: none;
  }
`
